import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { mfaMailIcon, mfaPhoneIcon, mfaTextIcon } from '@ld.consumer-portal/data-uri';
import { KeyboardService, PlatformService } from '@ld.consumer-portal/utils';
import { MfaCardComponent, MfaOption, MfaVerificationRequest } from '../mfa-card/mfa-card.component';
import { VerificationCodeCardComponent, VerificationCodeSubmit } from '../verification-code-card/verification-code-card.component';

export type MfaDialogMode = 'select-method' | 'continue';

export interface MfaDialogData {
  title?: string;
  description?: string;
  showFaqLink?: boolean;
  showMfaInfoIcon?: boolean;
  accountId?: string;
  mode?: MfaDialogMode;
  emailOptions: MfaOption[];
  textPhoneOptions: MfaOption[];
  callPhoneOptions?: MfaOption[];
}

export type MfaDialogResult =
  | { action: 'cancel' }
  | { action: 'continue' }
  | { action: 'verify-request'; request: MfaVerificationRequest }
  | { action: 'verify-code'; code: string; rememberDevice: boolean }
  | { action: 'resend' };

@Component({
  selector: 'ld-mfa-dialog',
  template: `
    @if (isBottomSheet()) {
      <div class="fixed inset-0 z-[70] flex items-end keyboard-aware-container"
        [style.padding-bottom.px]="keyboardHeight()">
      <div
        class="bg-white w-full rounded-t-2xl shadow-xl flex flex-col overflow-hidden mfa-bottom-sheet-slide-up"
        [style.max-height]="'calc(100dvh - env(safe-area-inset-top, 0px) - ' + keyboardHeight() + 'px)'"
        style="padding-bottom: env(safe-area-inset-bottom, 0px)"
      >
        @if (data.title) {
          <div class="flex items-center justify-between px-4 py-3 bg-black rounded-t-2xl shrink-0">
            <span class="font-dm text-base font-semibold text-white">{{ data.title }}</span>
            <button
              type="button"
              class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
              aria-label="Close"
              (click)="onCancel()"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
        <div class="p-6 overflow-y-auto flex-1" *transloco="let t">
          @if (step() === 'select-method') {
            @if (errorMessage()) {
              <div
                role="alert"
                class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
              >
                {{ errorMessage() }}
              </div>
            }
            <p class="font-dm text-sm text-ld-neutral-500 mb-6 text-left">
              {{ data.description }}
            </p>
            @if (mode() === 'continue') {
              <div class="flex flex-col gap-3">
                <button
                  id="mfa-bs-continue-btn"
                  type="button"
                  class="btn-primary-ld-purple mfa-bs-action-btn order-1"
                  (click)="onContinue()"
                >
                  <span class="mfa-bs-btn-label">{{ t('commonButtonsContinue') | uppercase }}</span>
                </button>
                <button
                  id="mfa-bs-cancel-btn"
                  type="button"
                  class="btn-outline-ld-purple mfa-bs-action-btn order-2"
                  (click)="onCancel()"
                >
                  <span class="mfa-bs-btn-label">{{ t('commonButtonsCancel') }}</span>
                </button>
              </div>
            } @else {
              <div class="flex flex-col gap-4 mb-6">
                @if (data.emailOptions.length > 0) {
                  <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                    <mat-label>{{ t('mfaMethodsEmail') }}</mat-label>
                    <mat-select [value]="initialEmail()" (selectionChange)="selectedEmail.set($event.value)">
                      @for (option of data.emailOptions; track option.value) {
                        <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
                @if (data.textPhoneOptions.length > 0) {
                  <mat-form-field appearance="outline" class="w-full mfa-bs-dropdown">
                    <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
                    <mat-select [value]="initialPhone()" (selectionChange)="selectedPhone.set($event.value)">
                      @for (option of data.textPhoneOptions; track option.value) {
                        <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
              </div>
              @if (data.showFaqLink) {
                <p class="text-left font-dm text-xs text-ld-neutral-500 mb-6">
                  {{ t('mfaFaqText1') }}
                  <a
                    [routerLink]="faqLink()"
                    target="_blank"
                    class="text-ld-primary underline cursor-pointer hover:text-ld-primary-hover"
                  >
                    {{ t('mfaFaqLink') }}
                  </a>
                  {{ t('mfaFaqText2') }}
                </p>
              }
              <div class="flex flex-col gap-3">
                @if (data.emailOptions.length > 0) {
                  <button
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onSelectEmail()"
                  >
                    <span class="mfa-bs-btn-icon"><img [src]="mailIcon" width="20" height="20" alt="" aria-hidden="true" /></span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaEmail') | uppercase }}</span>
                  </button>
                }
                @if (data.textPhoneOptions.length > 0) {
                  <button
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onSelectText()"
                  >
                    <span class="mfa-bs-btn-icon"><img [src]="textIcon" width="20" height="20" alt="" aria-hidden="true" /></span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaText') | uppercase }}</span>
                  </button>
                }
                @if ((data.callPhoneOptions ?? []).length > 0) {
                  <button
                    type="button"
                    class="btn-primary-ld-purple mfa-bs-action-btn"
                    (click)="onSelectCall()"
                  >
                    <span class="mfa-bs-btn-icon"><img [src]="phoneIcon" width="20" height="20" alt="" aria-hidden="true" /></span>
                    <span class="mfa-bs-btn-label">{{ t('mfaReceiveViaCall') | uppercase }}</span>
                  </button>
                }
              </div>
            }
          } @else if (step() === 'sending-code') {
            <div class="flex flex-col items-center justify-center py-8">
              <mat-spinner diameter="48"></mat-spinner>
              <p class="mt-4 text-ld-neutral-500 font-dm text-sm">Sending verification code...</p>
            </div>
          } @else {
            <ld-verification-code-card
              [embedded]="true"
              [showLogo]="false"
              [showTitle]="false"
              [maskedContact]="maskedContact()"
              [verificationType]="verificationType()"
              [showRememberDevice]="false"
              [showFaqLink]="data.showFaqLink ?? false"
              [accountId]="data.accountId ?? ''"
              (verify)="onVerifyCode($event)"
              (resend)="onResend()"
              (cancel)="onCancel()"
            />
          }
        </div>
      </div>
      </div>
    } @else {
      <div
        class="bg-white w-full rounded-2xl shadow-xl flex flex-col overflow-hidden"
        style="max-width: 680px; max-height: calc(100dvh - 2rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))"
      >
        @if (data.title) {
          <header class="flex items-center justify-between gap-6 px-6 py-4 border-b border-ld-border shrink-0">
            <h2 class="text-xl font-dm font-medium text-ld-neutral leading-7 m-0">
              {{ data.title }}
            </h2>
          </header>
        }
        <div class="p-6 overflow-y-auto flex-1" *transloco="let t">
          @if (step() === 'select-method') {
            @if (errorMessage()) {
              <div
                role="alert"
                class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
              >
                {{ errorMessage() }}
              </div>
            }
            @if (mode() === 'continue') {
              <p class="font-dm text-sm text-ld-neutral-500 mb-6 text-left">
                {{ data.description }}
              </p>
              <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-center md:gap-4">
                <button
                  id="mfa-cancel-btn"
                  type="button"
                  class="w-full md:w-auto bg-transparent border-0 text-ld-neutral-700 cursor-pointer py-3 md:py-0 font-dm text-sm font-bold uppercase tracking-wide text-center hover:text-ld-neutral-900 focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded"
                  (click)="onCancel()"
                >
                  {{ t('commonButtonsCancel') }}
                </button>
                <button
                  id="mfa-continue-btn"
                  type="button"
                  class="btn-primary-ld-purple"
                  (click)="onContinue()"
                >
                  {{ t('commonButtonsContinue') | uppercase }}
                </button>
              </div>
            } @else {
              <ld-mfa-card
                [embedded]="true"
                [showLogo]="false"
                [showTitle]="false"
                [leftAlign]="true"
                [title]="data.title ?? ''"
                [description]="data.description ?? ''"
                [showFaqLink]="data.showFaqLink ?? false"
                [showMfaInfoIcon]="data.showMfaInfoIcon ?? true"
                [accountId]="data.accountId ?? ''"
                [emailOptions]="data.emailOptions"
                [textPhoneOptions]="data.textPhoneOptions"
                [callPhoneOptions]="data.callPhoneOptions ?? []"
                (emailVerification)="onVerificationRequest($event)"
                (textVerification)="onVerificationRequest($event)"
                (callVerification)="onVerificationRequest($event)"
                (cancel)="onCancel()"
              />
            }
          } @else if (step() === 'sending-code') {
            <div class="flex flex-col items-center justify-center py-8">
              <mat-spinner diameter="48"></mat-spinner>
              <p class="mt-4 text-ld-neutral-500 font-dm text-sm">Sending verification code...</p>
            </div>
          } @else {
            <ld-verification-code-card
              [embedded]="true"
              [showLogo]="false"
              [showTitle]="false"
              [maskedContact]="maskedContact()"
              [verificationType]="verificationType()"
              [showRememberDevice]="false"
              [showFaqLink]="data.showFaqLink ?? false"
              [accountId]="data.accountId ?? ''"
              (verify)="onVerifyCode($event)"
              (resend)="onResend()"
              (cancel)="onCancel()"
            />
          }
        </div>
      </div>
    }
  `,
  styles: `
    .keyboard-aware-container {
      transition: padding-bottom 0.25s ease-out;
    }
    .mfa-bottom-sheet-slide-up {
      animation: mfaBottomSheetSlideUp 0.3s ease-out;
      transition: max-height 0.25s ease-out;
    }
    @keyframes mfaBottomSheetSlideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    .mfa-bs-dropdown .mat-mdc-text-field-wrapper {
      height: 48px;
    }
    .mfa-bs-dropdown .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }
    .mfa-bs-action-btn {
      width: 100%;
      font-size: 13px !important;
      padding: 14px 16px !important;
      white-space: nowrap;
      height: 48px;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .mfa-bs-btn-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
    .mfa-bs-btn-label {
      text-align: center;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MfaCardComponent,
    VerificationCodeCardComponent,
    MatProgressSpinner,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslocoDirective,
    RouterLink,
    UpperCasePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaDialogComponent {
  private readonly dialogRef = inject<DialogRef<MfaDialogResult>>(DialogRef);
  private readonly platformService = inject(PlatformService);
  private readonly keyboardService = inject(KeyboardService);
  protected readonly data = inject<MfaDialogData>(DIALOG_DATA);

  protected readonly isBottomSheet = this.platformService.isNativeMobile;
  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  protected readonly mailIcon = mfaMailIcon;
  protected readonly textIcon = mfaTextIcon;
  protected readonly phoneIcon = mfaPhoneIcon;

  protected readonly selectedEmail = signal<MfaOption | null>(null);
  protected readonly selectedPhone = signal<MfaOption | null>(null);
  protected readonly selectedCallPhone = signal<MfaOption | null>(null);

  protected readonly initialEmail = computed(() => this.data.emailOptions[0] ?? null);
  protected readonly initialPhone = computed(() => this.data.textPhoneOptions[0] ?? null);
  protected readonly initialCallPhone = computed(() => (this.data.callPhoneOptions ?? [])[0] ?? null);

  protected readonly faqLink = computed(() => {
    const id = this.data.accountId;
    return id ? `/help/${id}/faqs` : '/dashboard';
  });

  protected readonly mode = computed<MfaDialogMode>(() => this.data.mode ?? 'select-method');

  protected readonly step = signal<'select-method' | 'sending-code' | 'enter-code'>('select-method');
  protected readonly maskedContact = signal('');
  protected readonly verificationType = signal<'email' | 'text'>('email');
  protected readonly errorMessage = signal<string | null>(null);

  constructor() {
    if (this.platformService.isNativeMobile()) {
      this.dialogRef.overlayRef.updateSize({ width: '100vw', maxWidth: '100vw' });
    }
  }

  protected onContinue(): void {
    this.dialogRef.close({ action: 'continue' });
  }

  protected onSelectEmail(): void {
    const option = this.selectedEmail() ?? this.initialEmail();
    if (option) {
      this.dialogRef.close({ action: 'verify-request', request: { method: 'email', value: option.value } });
    }
  }

  protected onSelectText(): void {
    const option = this.selectedPhone() ?? this.initialPhone();
    if (option) {
      this.dialogRef.close({ action: 'verify-request', request: { method: 'text', value: option.value } });
    }
  }

  protected onSelectCall(): void {
    const option = this.selectedCallPhone() ?? this.initialCallPhone();
    if (option) {
      this.dialogRef.close({ action: 'verify-request', request: { method: 'call', value: option.value } });
    }
  }

  onVerificationRequest(request: MfaVerificationRequest): void {
    this.dialogRef.close({ action: 'verify-request', request });
  }

  onVerifyCode(event: VerificationCodeSubmit): void {
    this.dialogRef.close({ action: 'verify-code', code: event.code, rememberDevice: event.rememberDevice });
  }

  onResend(): void {
    this.dialogRef.close({ action: 'resend' });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  showSendingCode(): void {
    this.step.set('sending-code');
  }

  showCodeEntry(maskedContact: string, verificationType: 'email' | 'text'): void {
    this.maskedContact.set(maskedContact);
    this.verificationType.set(verificationType);
    this.step.set('enter-code');
  }

  showError(message: string): void {
    this.errorMessage.set(message);
    this.step.set('select-method');
  }
}
